import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

const Breadcrumb = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const breadcrumbMap: { [key: string]: string } = {
    'test-series': 'Test Series',
    'free-agriculture-quiz': 'Free Agriculture Quiz',
    'blog': 'Blog',
    'features': 'Features',
    'products': 'Products',
    'testimonials': 'Testimonials',
    'pricing': 'Pricing',
    'about': 'About',
    'privacy-policy': 'Privacy Policy',
    'terms': 'Terms & Conditions',
    'profile': 'Profile',
  };

  function toTitleCase(str: string) {
    return str
      .replace(/-/g, ' ')
      .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  }

  // Special handling for quiz pages to show subject name instead of slug
  const getBreadcrumbText = (name: string, index: number) => {
    // If this is a quiz page with subject slug and ID
    if (pathnames[0] === 'free-agriculture-quiz' && pathnames.length >= 3 && index === 1) {
      // This is the subject slug, we'll show it as the subject name
      return toTitleCase(name);
    }
    
    // If this is the quiz ID (last part), we'll skip it in breadcrumb
    if (pathnames[0] === 'free-agriculture-quiz' && pathnames.length >= 3 && index === 2) {
      return null; // Don't show the ID in breadcrumb
    }
    
    return breadcrumbMap[name] || toTitleCase(name);
  };

  return (
    <div className="bg-green-50 dark:bg-green-900/20 border-b border-green-100 dark:border-green-800/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <nav className="flex items-center space-x-2 text-sm">
          <Link 
            to="/" 
            className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors duration-200"
          >
            Home
          </Link>
          {pathnames.map((name, index) => {
            const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
            const isLast = index === pathnames.length - 1;
            const breadcrumbText = getBreadcrumbText(name, index);

            // Skip rendering if breadcrumbText is null
            if (breadcrumbText === null) {
              return null;
            }

            return (
              <React.Fragment key={name}>
                <ChevronRight className="h-4 w-4 text-green-400 dark:text-green-500" />
                {isLast ? (
                  <span className="text-green-800 dark:text-green-200 font-medium">
                    {breadcrumbText}
                  </span>
                ) : (
                  <Link
                    to={routeTo}
                    className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 transition-colors duration-200"
                  >
                    {breadcrumbText}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default Breadcrumb; 